import { Container, Tabs, Tab, Box } from "@mui/material";
import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { useTranslation } from "../../../../explorer/hooks/useTranslation";
import { useReduxState } from "../../../../explorer/redux/hooks";
import { Machine } from "../../../../explorer/types/SpareParts";
import { BomItemDetails } from "../../../../explorer/types/SpareParts/BomItemDetails";
import { MachineDocument } from "../../../../explorer/types/SpareParts/MachineDocument";
import {
  AppIconsDocument,
  AppIconsMaintenance,
  AppIconsEmergency,
} from "../../../components/AppIcons";
import AppCustomTabPanel from "../../../components/AppTabPanel/AppCustomTabPanel";
import BreadcrumbComponent from "../../../components/Breadcrumb/BreadcrumbComponent";
import PartList, {
  ColumnAlign,
  ColumnWidth,
} from "../../../components/PartsList/PartList";
import BasketIconComponent from "../../../components/PartsList/TableLayout/TableColumnComponents/BasketIconComponent";
import ReplaceIconComponent from "../../../components/PartsList/TableLayout/TableColumnComponents/ReplaceIconComponent";
import { reduxStoreConfig } from "../../../configuration/reduxStoreConfig";
import ServiceProvider from "../../../configuration/ServiceProvider";
import useGoToPage from "../../../hook/useGoToPage";
import RenderBusinessUnitIcon from "../InstalledBasePage/InstalledBaseAccordion/InstalledBaseAccordionIcon";
import {
  useClasses,
  Root,
  MachineMoreBar,
  MachineMoreTypography,
  MachineMoreContainer,
  Content,
  TabsContent,
} from "./MachineMorePage.style";
import useBrands from "../../../../explorer/hooks/useBrands";
import useLanguages from "../../../../explorer/hooks/useLanguages";
import { usePartDetails } from "../../../hook/usePartDetails";
import MachineDocumentList from "./MachineDocumentList";
import MassiveAddButton from "../../../components/MassiveAddButton/MassiveAddButton";
import { StyleguideSavioColors } from "../../../configuration/themeSettings";

export enum MachineMoreType {
  TechnicalDocumentation = "TechnicalDocumentation",
  MaintenanceList = "MaintenanceList",
  EmergencyList = "EmergencyList",
}

const MachineMorePage = () => {
  const [value, setValue] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentMachine, setCurrentMachine] = useReduxState<Machine>(
    reduxStoreConfig.sparePartsCurrentMachine
  );
  const [rows, setRows] = useState<MachineDocument[] | BomItemDetails[]>([]);
  const classes = useClasses();
  const goToPage = useGoToPage();
  const { t } = useTranslation();
  const { customerSiteCode, machineId, tabIndex } = useParams();
  const brand = useBrands().currentBrandUrl;
  const lang = useLanguages().currentLanguage;

  useEffect(() => {
    setLoading(true);

    switch (tabIndex) {
      case MachineMoreType.TechnicalDocumentation:
        ServiceProvider.SpareParts.getTechnicalDocByMachineId(
          machineId!,
          undefined
        )
          .then((result) => {
            setRows(result);
            setLoading(false);
          })
          .catch((e) => console.error(e));
        setValue(0);
        break;
      case MachineMoreType.MaintenanceList:
        ServiceProvider.SpareParts.getMaintListByMachineId(machineId!, lang)
          .then((result) => {
            setRows(result);
            setLoading(false);
          })
          .catch((e) => console.error(e));
        setValue(1);
        break;
      case MachineMoreType.EmergencyList:
        ServiceProvider.SpareParts.getEmergListByMachineId(machineId!, lang)
          .then((result) => {
            setRows(result);
            setLoading(false);
          })
          .catch((e) => console.error(e));
        setValue(2);
        break;
    }
  }, [tabIndex, lang]);

  useEffect(() => {
    if (Object.keys(currentMachine).length === 0)
      ServiceProvider.SpareParts.getMachineById(brand, machineId!)
        .then((result) => setCurrentMachine(result))
        .catch((e) => console.error(e));
  }, [machineId]);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    let myTabIndex: MachineMoreType = MachineMoreType.TechnicalDocumentation;

    setValue(newValue);

    switch (newValue) {
      case 0:
        myTabIndex = MachineMoreType.TechnicalDocumentation;
        break;
      case 1:
        myTabIndex = MachineMoreType.MaintenanceList;
        break;
      case 2:
        myTabIndex = MachineMoreType.EmergencyList;
        break;
    }

    goToPage.spareParts.goToMachineMore(
      customerSiteCode!,
      machineId!,
      myTabIndex
    );
  };

  const { openPartDetails } = usePartDetails();

  const handleOpenPartDetail = (
    code: string,
    replacementCode: string,
    manufacturer: string
  ) => {
    openPartDetails(code, replacementCode, manufacturer);
  };

  return (
    <Root>
      <BreadcrumbComponent />
      <MachineMoreBar elevation={0}>
        <div>
          <MachineMoreTypography variant="h6" p={1}>
            <RenderBusinessUnitIcon status={currentMachine.businessUnit!} />
            {`${currentMachine.serialNumber} ${currentMachine.description}`}
          </MachineMoreTypography>
        </div>
      </MachineMoreBar>
      <MachineMoreContainer>
        <Container>
          <Content>
            <Tabs onChange={handleChange} value={value} sx={classes.tabs}>
              <Tab
                sx={classes.tab}
                icon={<AppIconsDocument />}
                iconPosition="start"
                label={t("common.spareparts.thecnicalDoc")}
                disabled={!currentMachine.hasDocument}
              />
              <Tab
                sx={classes.tab}
                icon={<AppIconsMaintenance />}
                iconPosition="start"
                label={t("common.spareparts.maintList")}
                disabled={!currentMachine.hasMaintList}
              />
              <Tab
                sx={classes.tab}
                icon={<AppIconsEmergency />}
                iconPosition="start"
                label={t("common.spareparts.emergList")}
                disabled={!currentMachine.hasEmergList}
              />
            </Tabs>

            <TabsContent>
              <MachineDocumentList
                rows={rows}
                tabsValue={value}
                loading={loading}
              />
              <AppCustomTabPanel value={value} index={1}>
                <Box>
                  <MassiveAddButton rows={rows as BomItemDetails[]} />
                  <PartList
                    items={rows}
                    loading={loading}
                    onRowClick={handleOpenPartDetail}
                    columns={[
                      {
                        name: "code",
                        viewHeader: true,
                        width: ColumnWidth.ShortTextWidth,
                        align: ColumnAlign.Left,
                      },
                      {
                        name: "description",
                        viewHeader: true,
                        align: ColumnAlign.Left,
                      },
                      {
                        name: "withreplacement",
                        viewHeader: false,
                        width: ColumnWidth.IconWidth,
                        customComponent: ReplaceIconComponent,
                        align: ColumnAlign.Right,
                      },
                      {
                        name: "showBasket",
                        viewHeader: false,
                        width: ColumnWidth.IconWidth,
                        customComponent: BasketIconComponent,
                        additionalProps: false,
                        align: ColumnAlign.Right,
                      },
                    ]}
                  />
                </Box>
              </AppCustomTabPanel>

              <AppCustomTabPanel value={value} index={2}>
                <Box>
                  <MassiveAddButton rows={rows as BomItemDetails[]} />
                  <PartList
                    items={rows}
                    loading={loading}
                    onRowClick={handleOpenPartDetail}
                    columns={[
                      {
                        name: "code",
                        viewHeader: true,
                        width: ColumnWidth.ShortTextWidth,
                        align: ColumnAlign.Left,
                      },
                      {
                        name: "description",
                        viewHeader: true,
                        align: ColumnAlign.Left,
                      },
                      {
                        name: "withreplacement",
                        viewHeader: false,
                        width: ColumnWidth.IconWidth,
                        customComponent: ReplaceIconComponent,
                        align: ColumnAlign.Right,
                      },
                      {
                        name: "showBasket",
                        viewHeader: false,
                        width: ColumnWidth.IconWidth,
                        customComponent: BasketIconComponent,
                        align: ColumnAlign.Right,
                      },
                    ]}
                  />
                </Box>
              </AppCustomTabPanel>
            </TabsContent>
          </Content>
        </Container>
      </MachineMoreContainer>
    </Root>
  );
};

export default MachineMorePage;
